import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, AlertCircle, RefreshCw, Loader2, ChevronRight, Target, Info, History,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useCalls, useScorecardSummary, evaluateCalls, rebuildCalls } from '../hooks/useCalls';
import {
  outcomeLabel, outcomeBadge, effectiveReturn, returnColor, formatPct, formatPrice,
  formatEntry, kindLabel, horizonLabel, daysOpen,
} from './callHelpers';

/**
 * Was Russ right? Every stated trade plan (and every daily-report call with
 * a target) tracked against what the price actually did.
 *
 * Two kinds are deliberately kept apart in the filter and the tiles:
 * "plan" scores Russ; "guidance" scores the report's reading of Russ.
 */
const Scorecard = () => {
  const navigate = useNavigate();
  const [kind, setKind] = useState('');      // '' | plan | guidance
  const [status, setStatus] = useState('');  // '' | open | closed
  const [busy, setBusy] = useState(null);    // null | 'evaluate' | 'rebuild'
  const [notice, setNotice] = useState(null);

  const { summary, loading: summaryLoading, error: summaryError, reload: reloadSummary } =
    useScorecardSummary({ kind: kind || undefined });
  const { calls, loading: callsLoading, error: callsError, reload: reloadCalls } =
    useCalls({ kind: kind || undefined, status: status || undefined, limit: 500 });

  const reload = () => { reloadSummary(); reloadCalls(); };

  const runEvaluate = async () => {
    setBusy('evaluate');
    setNotice(null);
    try {
      const res = await evaluateCalls();
      const s = res?.summary || {};
      setNotice(`Evaluated ${s.evaluated ?? 0} call(s) across ${s.symbols ?? 0} symbol(s); ${s.closed ?? 0} closed, ${s.noData ?? 0} without price data.`);
      reload();
    } catch (err) {
      setNotice(err?.message || 'Evaluation failed');
    } finally {
      setBusy(null);
    }
  };

  const runRebuild = async () => {
    setBusy('rebuild');
    setNotice(null);
    try {
      const res = await rebuildCalls(false);
      const r = res?.rebuild || {};
      setNotice(`Rebuilt from history: ${r.opened ?? 0} opened, ${r.amended ?? 0} amended, ${r.superseded ?? 0} superseded.`);
      reload();
    } catch (err) {
      setNotice(err?.message || 'Rebuild failed');
    } finally {
      setBusy(null);
    }
  };

  const stats = summary?.stats;
  const lastEvaluated = summary?.lastEvaluatedAt ? parseISO(summary.lastEvaluatedAt) : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Scorecard</h1>
          <p>Was Russ right? Each stated trade plan, tracked against what the price did next.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {lastEvaluated && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              Evaluated {format(lastEvaluated, 'MMM dd, h:mm a')}
            </span>
          )}
          <button className="btn btn-secondary" onClick={runEvaluate} disabled={busy !== null}>
            {busy === 'evaluate' ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
            Evaluate now
          </button>
        </div>
      </div>

      {notice && (
        <div style={{
          marginBottom: '1rem', padding: '0.625rem 0.875rem', borderRadius: '6px',
          background: 'var(--severity-info-bg)', borderLeft: '4px solid var(--severity-info-border)',
          fontSize: '0.875rem',
        }}>
          {notice}
        </div>
      )}

      {/* Filters */}
      <div className="filter-buttons" style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        <FilterGroup
          value={kind}
          onChange={setKind}
          options={[
            { id: '', label: 'All sources' },
            { id: 'plan', label: "Russ's plans" },
            { id: 'guidance', label: 'Report guidance' },
          ]}
        />
        <span style={{ width: '0.75rem' }} />
        <FilterGroup
          value={status}
          onChange={setStatus}
          options={[
            { id: '', label: 'All' },
            { id: 'open', label: 'Open' },
            { id: 'closed', label: 'Closed' },
          ]}
        />
      </div>

      {summaryError && <ErrorBlock message={summaryError} />}

      {/* Tiles */}
      {summaryLoading ? <Spinner /> : stats && (
        <>
          <div className="stat-grid">
            <Tile
              label="Hit rate"
              value={stats.hitRatePct != null ? `${stats.hitRatePct}%` : '—'}
              sub={stats.resolved > 0
                ? `${stats.wins} of ${stats.resolved} resolved (${stats.targetHit} target, ${stats.partialThenStop} partial, ${stats.stopHit} stopped)`
                : 'Nothing has resolved on a level yet'}
            />
            <Tile
              label="Avg return"
              value={formatPct(stats.avgReturnPct)}
              valueColor={returnColor(stats.avgReturnPct)}
              sub={`closed ${formatPct(stats.avgClosedReturnPct)} · open ${formatPct(stats.avgOpenReturnPct)}`}
            />
            <Tile
              label="Calls"
              value={stats.total}
              sub={`${stats.open} open · ${stats.closed} closed · ${stats.superseded} superseded · ${stats.returnOnly} return-only`}
            />
            <Tile
              label="Median days to resolve"
              value={stats.medianDaysToResolve != null ? Math.round(stats.medianDaysToResolve) : '—'}
              sub="reference date to stop or final target"
            />
          </div>

          {/* Checkpoint returns */}
          {Object.keys(stats.avgCheckpointReturnPct || {}).length > 0 && (
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <SectionTitle icon={<History size={16} />} title="Average return after" />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
                {['7', '30', '90', '180', '365'].map((d) => {
                  const v = stats.avgCheckpointReturnPct?.[d];
                  if (v == null) return null;
                  return (
                    <div key={d} style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                      <span style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {d} days
                      </span>
                      <strong style={{ fontSize: '1.125rem', color: returnColor(v) }}>{formatPct(v)}</strong>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {stats.checkpointCounts?.[d]} call{stats.checkpointCounts?.[d] === 1 ? '' : 's'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Breakdowns */}
          {(summary.byKind?.length > 1 || summary.byHorizon?.length > 1) && (
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <SectionTitle icon={<Target size={16} />} title="Breakdown" />
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Slice</th><th>Calls</th><th>Resolved</th><th>Hit rate</th><th>Avg return</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary.byKind || []).map((g) => <BreakdownRow key={`k-${g.key}`} label={kindLabel(g.key)} stats={g.stats} />)}
                    {(summary.byHorizon || []).map((g) => <BreakdownRow key={`h-${g.key}`} label={horizonLabel(g.key) || 'no horizon stated'} stats={g.stats} />)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Calls table */}
      <div className="card">
        <SectionTitle icon={<Trophy size={16} />} title={`Calls${calls.length ? ` (${calls.length})` : ''}`} />
        {callsError && <ErrorBlock message={callsError} />}
        {callsLoading ? <Spinner /> : calls.length === 0 ? (
          <div className="empty-state">
            <Info size={40} />
            <p>No calls tracked yet{kind || status ? ' for this filter' : ''}.</p>
            {!kind && !status && (
              <>
                <p style={{ fontSize: '0.875rem' }}>
                  Calls are created as messages and reports come in. To backfill from the trade plans already
                  stored, rebuild from history. This takes a minute and fetches prices for every symbol.
                </p>
                <button className="btn btn-primary" onClick={runRebuild} disabled={busy !== null}>
                  {busy === 'rebuild' ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                  Rebuild from history
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="calls-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Source</th>
                  <th>Opened</th>
                  <th>Levels</th>
                  <th>Ref</th>
                  <th>Now / Close</th>
                  <th>Return</th>
                  <th>Outcome</th>
                  <th>Days</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <CallTableRow
                    key={c.id}
                    call={c}
                    onClick={() => navigate(`/mentions/tickers/${encodeURIComponent(c.symbol)}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const CallTableRow = ({ call, onClick }) => {
  const ret = effectiveReturn(call);
  const opened = call.openedAt ? parseISO(call.openedAt) : null;
  const closeOrNow = call.status === 'closed' && call.outcome ? call.outcome.closePrice : call.currentPrice;
  const hits = (call.levelHits || []).length;
  const days = daysOpen(call);

  return (
    <tr onClick={onClick} style={{ cursor: 'pointer' }}>
      <td>
        <strong style={{ color: 'var(--primary-color)' }}>{call.symbol}</strong>
        {call.displayName && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{call.displayName}</div>
        )}
      </td>
      <td>
        <span className={`badge ${call.kind === 'guidance' ? 'badge-accent' : 'badge-info'}`}>
          {call.kind === 'guidance' ? 'Report' : 'Russ'}
        </span>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
          {call.direction}{call.horizon ? ` · ${horizonLabel(call.horizon)}` : ''}
        </div>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{opened ? format(opened, 'MMM dd, yyyy') : '—'}</td>
      <td style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
        <LevelsInline call={call} />
      </td>
      <td>{formatPrice(call.referencePrice)}</td>
      <td>{formatPrice(closeOrNow)}</td>
      <td style={{ color: returnColor(ret), fontWeight: 600, whiteSpace: 'nowrap' }}>
        {formatPct(ret)}
        {call.status === 'open' && call.maxFavorablePct != null && (
          <div style={{ fontSize: '0.6875rem', fontWeight: 400, color: 'var(--text-secondary)' }}>
            peak {formatPct(call.maxFavorablePct)} · trough {formatPct(call.maxAdversePct)}
          </div>
        )}
      </td>
      <td>
        <span className={`badge ${outcomeBadge(call)}`}>{outcomeLabel(call)}</span>
        {hits > 0 && (
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
            {hits} level{hits === 1 ? '' : 's'} hit
          </div>
        )}
        {call.evaluationError && (
          <div style={{ fontSize: '0.6875rem', color: 'var(--warning-color)', marginTop: '0.25rem' }}>
            no price data
          </div>
        )}
      </td>
      <td>{days ?? '—'}</td>
      <td><ChevronRight size={16} style={{ color: 'var(--text-secondary)' }} /></td>
    </tr>
  );
};

/** Entry / TP / SL on one line, only the parts that exist. */
export const LevelsInline = ({ call }) => {
  const parts = [];
  const entry = formatEntry(call);
  if (entry) parts.push(`entry ${entry}`);
  if (call.takeProfit && call.takeProfit.length > 0) parts.push(`TP ${call.takeProfit.map(formatPrice).join(' / ')}`);
  if (call.stopLoss != null) parts.push(`SL ${formatPrice(call.stopLoss)}`);
  if (parts.length === 0) return <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>no levels</span>;
  return <>{parts.join(' · ')}</>;
};

const BreakdownRow = ({ label, stats }) => (
  <tr>
    <td>{label}</td>
    <td>{stats.total}</td>
    <td>{stats.resolved}</td>
    <td>{stats.hitRatePct != null ? `${stats.hitRatePct}%` : '—'}</td>
    <td style={{ color: returnColor(stats.avgReturnPct) }}>{formatPct(stats.avgReturnPct)}</td>
  </tr>
);

const Tile = ({ label, value, sub, valueColor }) => (
  <div className="stat-card">
    <div className="stat-label">{label}</div>
    <div className="stat-value" style={valueColor ? { color: valueColor } : undefined}>{value}</div>
    {sub && <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{sub}</div>}
  </div>
);

const FilterGroup = ({ value, onChange, options }) => (
  <>
    {options.map((o) => (
      <button
        key={o.id}
        className={`btn ${value === o.id ? 'btn-primary' : 'btn-secondary'}`}
        onClick={() => onChange(o.id)}
        style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
      >
        {o.label}
      </button>
    ))}
  </>
);

const SectionTitle = ({ icon, title }) => (
  <h2 style={{
    fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginTop: 0, marginBottom: '0.875rem',
    display: 'flex', alignItems: 'center', gap: '0.375rem',
  }}>
    {icon}{title}
  </h2>
);

const Spinner = () => <div className="loading"><div className="spinner"></div></div>;

const ErrorBlock = ({ message }) => (
  <div className="error" style={{ marginBottom: '1rem' }}>
    <AlertCircle size={20} />
    <p>{message}</p>
  </div>
);

export default Scorecard;
